import type { Retreat, Stance } from '../types';

export interface StanceDef {
  id: Stance;
  name: string;
  line: string;
  risk: string;
  /** Multipliers and bonuses applied while this stance is active (see "Meta stavova"). */
  mods: {
    attack?: number; // multiplicative, 0.1 = +10%
    per?: number;
    defense?: number;
    loot?: number;
    stealth?: number; // additive
    escape?: number; // additive
    encounter?: number; // multiplier on encounter chance
    ambush?: number; // additive ambush damage bonus
  };
  strongVs: Stance | null;
  weakVs: Stance | null;
  icon: string; // sprite id
}

export const STANCES_DEF: Record<Stance, StanceDef> = {
  peaceful: {
    id: 'peaceful',
    name: 'Peaceful',
    line: 'Offers trade, flees if refused.',
    risk: 'Low risk · loses the least · never takes PvP loot',
    mods: { escape: 0.2, encounter: 0.9 },
    strongVs: null,
    weakVs: null,
    icon: 'ui_stance_peaceful',
  },
  cautious: {
    id: 'cautious',
    name: 'Cautious',
    line: 'Attacks only weaker survivors, flees from stronger.',
    risk: 'Medium risk · +15% PER · −10% loot',
    // GDD (krug 3, L4 / O6): careful searching finds a bit less; no defense penalty.
    mods: { per: 0.15, loot: -0.1 },
    strongVs: 'shadow',
    weakVs: 'aggressive',
    icon: 'ui_stance_cautious',
  },
  aggressive: {
    id: 'aggressive',
    name: 'Aggressive',
    line: 'Attacks almost anyone.',
    risk: 'High risk · +10% attack · −10% PER',
    mods: { attack: 0.1, per: -0.1, encounter: 1.1 },
    strongVs: 'cautious',
    weakVs: 'shadow',
    icon: 'ui_stance_aggressive',
  },
  shadow: {
    id: 'shadow',
    name: 'Shadow',
    line: 'Avoids fights, follows survivors carrying loot, waits to ambush.',
    risk: 'High reward · stealth and ambush bonus · −15% defense',
    mods: { stealth: 0.1, ambush: 0.1, defense: -0.15, encounter: 1.2 },
    strongVs: 'aggressive',
    weakVs: 'cautious',
    icon: 'ui_stance_shadow',
  },
};

export const RETREAT_FRACTION: Record<Retreat, number> = { '50': 0.5, '25': 0.25, never: 0 };

/**
 * "Fight to the end" still runs from a fight at 15% HP to stay alive (krug 3, L6 / O8),
 * against enemies and survivors alike; 25% and 50% run earlier. `LAST_STAND.flee`: the same number, which the
 * simulations can change (3. 10. 2026, package 2: `FLEE_NEVER=` in `sim:runs`).
 */
export const LAST_STAND = { flee: 0.15 };
export const LAST_STAND_FLEE = LAST_STAND.flee;

/** HP share at which a survivor tries to get out of a fight. */
export function fleeAt(retreat: Retreat): number {
  return Math.max(RETREAT_FRACTION[retreat], LAST_STAND.flee);
}

/** Power ratio thresholds from the design doc. */
export const STANCE_THRESHOLDS = {
  cautiousAttackBelow: 0.8,
  cautiousFleeAbove: 1.0,
  aggressiveFleeAbove: 1.3,
} as const;
